import type { ILogger } from './types';

/**
 * Unified logger for both client and server
 *
 * Formats logs as structured JSON for proper Datadog parsing.
 * API remains simple - just pass message and optional error/context.
 */
class Logger implements ILogger {
	/**
	 * Format log entry as structured JSON
	 * Follows Datadog's standard error format
	 */
	private formatLog(
		level: string,
		message: unknown,
		...optionalParams: unknown[]
	): string {
		const logEntry: Record<string, unknown> = {
			level, // Explicitly set log level for Datadog
			message,
		};

		// Find first Error object and add as nested error field (Datadog convention)
		const error = optionalParams.find((param) => param instanceof Error);
		if (error) {
			logEntry.error = {
				kind: error.name,
				message: error.message,
				stack: error.stack,
			};
		}

		// Merge all plain objects as additional context
		optionalParams.forEach((param) => {
			if (
				param &&
				typeof param === 'object' &&
				!Array.isArray(param) &&
				!(param instanceof Error)
			) {
				Object.assign(logEntry, param);
			}
		});

		return JSON.stringify(logEntry);
	}

	/**
	 * Log debug message (development only)
	 * @param message - Main message (use template strings for dynamic values)
	 * @param optionalParams - Error objects and/or context objects
	 * @example
	 * logger.debug(`Processing ${count} items`);
	 * logger.debug('Cache stats', { hits: 150, misses: 20, hitRate: 0.88 });
	 */
	debug(message?: unknown, ...optionalParams: unknown[]): void {
		console.debug(this.formatLog('debug', message, ...optionalParams));
	}

	/**
	 * Log informational message
	 * @param message - Main message (use template strings for dynamic values)
	 * @param optionalParams - Error objects and/or context objects
	 * @example
	 * logger.info('User logged in');
	 * logger.info(`Scene created: ${sceneId}`);
	 * logger.info('Request completed', { duration: 123, status: 200 });
	 */
	info(message?: unknown, ...optionalParams: unknown[]): void {
		console.info(this.formatLog('info', message, ...optionalParams));
	}

	/**
	 * Log warning message
	 * @param message - Main message (use template strings for dynamic values)
	 * @param optionalParams - Error objects and/or context objects
	 * @example
	 * logger.warn('API rate limit approaching');
	 * logger.warn(`Rate limit: ${remaining} requests left`);
	 * logger.warn('Request failed', error, { endpoint: '/api/data', retries: 3 });
	 */
	warn(message?: unknown, ...optionalParams: unknown[]): void {
		console.warn(this.formatLog('warn', message, ...optionalParams));
	}

	/**
	 * Log error message
	 * @param message - Main message (use template strings for dynamic values)
	 * @param optionalParams - Error objects and/or context objects
	 * @example
	 * logger.error('Operation failed', error);
	 * logger.error('Upload failed', error, { assetId: 'asset-123', fileSize: 5000, attempts: 3 });
	 * logger.error(`Database connection lost: ${dbName}`);
	 */
	error(message?: unknown, ...optionalParams: unknown[]): void {
		console.error(this.formatLog('error', message, ...optionalParams));
	}
}

// Singleton instance
export const logger = new Logger();
